import { Component, Inject, OnInit, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: "app-billing-input",
  templateUrl: "./billing-input.component.html",
  styleUrls: ["./billing-input.component.scss"],
})
export class BillingInputComponent implements OnInit {
  names = this.data.names?.length ? [...new Set(this.data.names)].map(name => ({ key: name, value: name })) : [];
  isUsers = this.data.isUsers;
  namesString = this.data.isUsers ? "Choose Users" : "Choose Groups";
  calendar = inject(NgbCalendar);
  formatter = inject(NgbDateParserFormatter);
  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = this.calendar.getPrev(
    this.calendar.getToday(),
    "d",
    30
  );
  maxDate = this.calendar.getToday();
  toDate: NgbDate | null = this.maxDate;
  selectedNames = this.data.selectedNames;
  periodTypes = [
    { key: "Last 7 Days", value: "7" },
    { key: "Last 15 Days", value: "15" },
    { key: "Last 30 Days", value: "30" },
    { key: "Last 60 Days", value: "60" },
    { key: "Last 90 Days", value: "90" },
    { key: "Custom", value: "0" },
  ];
  billingTypes = [
    { key: "All", value: "-1" },
    { key: "Job", value: "0" },
    { key: "Session", value: "1" },
  ];
  billingForm: FormGroup = new FormGroup({
    names: new FormControl(this.selectedNames, [Validators.required]),
    period: new FormControl(this.periodTypes[2].value, [Validators.required]),
    startDate: new FormControl(this.fromDate),
    endDate: new FormControl(this.toDate),
    type: new FormControl("-1", [Validators.required]),
  });
  namesSettings = {
    singleSelection: false,
    idField: "value",
    textField: "key",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    itemsShowLimit: 2,
    allowSearchFilter: true,
    appendTo: "body",
  };
  periodSettings = {
    singleSelection: true,
    idField: "value",
    textField: "key",
    itemsShowLimit: 1,
    allowSearchFilter: false,
  };

  constructor(
    public dialog: MatDialogRef<BillingInputComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      names: string[];
      selectedNames: string;
      isUsers: boolean;
    },
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {}

  onClose() {
    this.names = [];
    this.dialog.close(false);
  }

  onPreview() {
    if (!this.billingForm.valid) {
      this.snackBar.open("Please fill all the required fields", "Close", {
        duration: 3000,
      });
      return;
    }

    if (
      this.billingForm.value.period === "0" &&
      !this.billingForm.value.startDate
    ) {
      this.snackBar.open("Please select date range", "Close", {
        duration: 3000,
      });
      return;
    }

    // if end date is not selected then set it to startDate
    if (!this.billingForm.value.endDate) {
      this.billingForm.controls.endDate.setValue(
        this.billingForm.value.startDate
      );
    }

    // if period is not 0 then calculate start and end date using period value
    if (this.billingForm.value.period !== "0") {
      const period = this.billingForm.value.period;
      const endDate = this.calendar.getToday();
      const startDate = this.calendar.getPrev(endDate, "d", period);
      this.billingForm.controls.startDate.setValue(
        this.formatter.format(startDate)
      );
      this.billingForm.controls.endDate.setValue(
        this.formatter.format(endDate)
      );
    } else {
      const startDate = this.billingForm.controls.startDate.value;
      const endDate = this.billingForm.controls.endDate.value;
      if (typeof startDate !== "string") {
        this.billingForm.controls.startDate.setValue(
          this.formatter.format(startDate)
        );
      }
      if (typeof endDate !== "string") {
        this.billingForm.controls.endDate.setValue(
          this.formatter.format(endDate)
        );
      }
    }

    this.dialog.close(this.billingForm.value);
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this.billingForm.controls.startDate.setValue(
        this.formatter.format(this.fromDate)
      );
    } else if (
      this.fromDate &&
      !this.toDate &&
      date &&
      date.after(this.fromDate)
    ) {
      this.toDate = date;
      this.billingForm.controls.endDate.setValue(
        this.formatter.format(this.toDate)
      );
    } else {
      this.toDate = null;
      this.fromDate = date;
      this.billingForm.controls.startDate.setValue(
        this.formatter.format(this.fromDate)
      );
    }
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  public onSelectAll() {
    const selected = this.names.map((item) => item.key);
    this.billingForm.get("names").patchValue(selected);
  }

  public onClearAll() {
    this.billingForm.get("names").patchValue([]);
  }
}
